import React from 'react';

// Custom simple icons to replace lucide-react
const X = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const ChevronLeft = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
);

const ChevronRight = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
);

const Download = ({ className }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
);

export interface ViewerImage {
    id: string;
    src: string;
    prompt?: string;
    width?: number;
    height?: number;
}

interface FullscreenImageViewerProps {
    isOpen: boolean;
    images: ViewerImage[];
    currentIndex: number;
    onClose: () => void;
    onNavigate: (index: number) => void;
    onDownload?: (imageId: string) => void;
}

const FullscreenImageViewer: React.FC<FullscreenImageViewerProps> = ({
    isOpen,
    images,
    currentIndex,
    onClose,
    onNavigate,
    onDownload
}) => {
    if (!isOpen || images.length === 0) return null;

    const currentImage = images[currentIndex];

    const handlePrev = () => {
        const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
        onNavigate(newIndex);
    };

    const handleNext = () => {
        const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
        onNavigate(newIndex);
    };

    // Add global keyboard event handler
    React.useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, currentIndex, onClose]);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={onClose}
        >
            {/* Top-right actions */}
            <div className="absolute top-4 right-4 flex gap-2 z-60">
                {onDownload && (
                    <button
                        className="p-2 text-white hover:text-snapfy-text-secondary transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDownload(currentImage.id);
                        }}
                        aria-label="Download image"
                    >
                        <Download className="w-6 h-6" />
                    </button>
                )}
                <button
                    className="p-2 text-white hover:text-snapfy-text-secondary transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                    }}
                    aria-label="Close"
                >
                    <X className="w-6 h-6" />
                </button>
            </div>

            {/* Navigation arrows */}
            {images.length > 1 && (
                <>
                    <button
                        className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white hover:text-snapfy-text-secondary transition-colors z-60"
                        onClick={(e) => {
                            e.stopPropagation();
                            handlePrev();
                        }}
                        aria-label="Previous image"
                    >
                        <ChevronLeft className="w-8 h-8" />
                    </button>
                    <button
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white hover:text-snapfy-text-secondary transition-colors z-60"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleNext();
                        }}
                        aria-label="Next image"
                    >
                        <ChevronRight className="w-8 h-8" />
                    </button>
                </>
            )}

            {/* Image container */}
            <div
                className="relative max-w-full max-h-full flex items-center justify-center p-4"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={currentImage.src}
                    alt={`Imagem ${currentIndex + 1}`}
                    className="max-w-full max-h-[90vh] object-contain select-none"
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                />
            </div>

            {/* Bottom info bar */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-linear-to-t from-black/80 to-transparent">
                <div className="text-white text-center flex items-center justify-center gap-3">
                    <p className="font-medium">
                        {currentIndex + 1} / {images.length}
                    </p>
                    {currentImage.width && currentImage.height && (
                        <span className="text-xs text-snapfy-text-secondary bg-black/40 px-2 py-0.5 rounded">
                            {currentImage.width} × {currentImage.height}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FullscreenImageViewer;
